import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ArrowLeft, Check, Loader2, Globe, User, Mail, Lock, Clock, Eye, EyeOff, Key, AppleIcon, AlertCircle, Badge } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import AppleLogin from 'react-apple-login';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isGoogleSignInBlocked, configureWebviewForAuth } from '@/utils/webview-detection';
import { getDeviceInfo, DeviceInfo } from '@/utils/device-detection';
import GoogleWebviewAuth from '@/components/auth/GoogleWebviewAuth';
import {
  getIpInfo,
  IpInfo,
  countries,
  countries_fr,
  countryTimezones,
  defaultTimezones,
  formatTimezone
} from '@/utils/ip-detection';
import { BASE_URL } from '@/config/api';
import { toast } from "@/hooks/use-toast";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface RegisterFormInputs {
  schoolName: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  country: string;
  timezone: string;
  accountType: string;
  studentToken: string;
}

const RegisterTeacher = () => {
  const { t, locale } = useLanguage();
  const { register: registerUser, loginWithGoogle, loginWithApple, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isInBlockingWebview = isGoogleSignInBlocked();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [country, setCountry] = useState('US');
  const [timezone, setTimezone] = useState('');
  const [ipInfo, setIpInfo] = useState<IpInfo | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isLoadingIp, setIsLoadingIp] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [availableTimezones, setAvailableTimezones] = useState<string[]>([]);
  const [formData, setFormData] = useState<RegisterFormInputs>({
    schoolName: '',
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    country: 'US',
    timezone: '',
    accountType: 'Student',
    studentToken: '',
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(true); // New state for token loading

  const isValidEmail = (email: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};

    if (!formData.firstName) errors.firstName = t('validation.required');
    if (!formData.lastName) errors.lastName = t('validation.required');
    if (!formData.username) errors.username = t('validation.required');
    /*if (!formData.email) {
      errors.email = t('validation.required');
    } else if (!isValidEmail(formData.email)) {
      errors.email = t('validation.invalidEmail');
    }*/
    if (!formData.password) {
      errors.password = t('validation.required');
    } else if (formData.password.length < 6) {
      errors.password = t('validation.passwordTooShort');
    }
    if (!formData.country) errors.country = t('validation.required');
    if (!formData.timezone) errors.timezone = t('validation.required');

    return errors;
  };

  const validateField = (name: keyof RegisterFormInputs, value: string) => {
    let error = '';

    switch(name) {
      case 'firstName':
      case 'lastName':
      case 'username':
      case 'country':
      case 'timezone':
        if (!value) error = t('validation.required');
        break;
      case 'email':
        if (!value) {
          error = t('validation.required');
        } else if (!isValidEmail(value)) {
          error = t('validation.invalidEmail');
        }
        break;
      case 'password':
        if (!value) {
          error = t('validation.required');
        } else if (value.length < 6) {
          error = t('validation.passwordTooShort');
        }
        break;
    }

    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/agenda', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    const fetchIpAndDeviceInfo = async () => {
      try {
        const deviceData = getDeviceInfo();
        setDeviceInfo(deviceData);
        console.log('Device information:', deviceData);

        setIsLoadingIp(true);
        const ipData = await getIpInfo();
        setIpInfo(ipData);

        const detectedCountry = ipData.countryCode || 'US';
        setCountry(detectedCountry);
        setFormData(prev => ({...prev, country: detectedCountry}));

        console.log('IP information:', ipData);
      } catch (error) {
        console.error('Error fetching IP or device info:', error);
      } finally {
        setIsLoadingIp(false);
      }
    };

    fetchIpAndDeviceInfo();
  }, []);

  useEffect(() => {
    const timezones = countryTimezones[country] || defaultTimezones;
    setAvailableTimezones(timezones);

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (timezones.includes(userTimezone)) {
        setTimezone(userTimezone);
        setFormData(prev => ({...prev, timezone: userTimezone}));
      } else if (timezones.length > 0) {
        setTimezone(timezones[0]);
        setFormData(prev => ({...prev, timezone: timezones[0]}));
      }
    } catch (error) {
      if (timezones.length > 0) {
        setTimezone(timezones[0]);
        setFormData(prev => ({...prev, timezone: timezones[0]}));
      }
    }
  }, [country]);

  useEffect(() => {
    if (formData.firstName && formData.lastName) {
      const generatedUsername = `${formData.firstName.toLowerCase()}${formData.lastName.toLowerCase()}`.replace(/\s+/g, '');
      setFormData(prev => ({...prev, username: generatedUsername}));

      if (formSubmitted) {
        validateField('username', generatedUsername);
      }
    }
  }, [formData.firstName, formData.lastName, formSubmitted]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setErrorMessage(t('register.tokenMissing'));
      setIsValidToken(false);
      setIsLoadingToken(false); // Set loading to false if no token
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/invite/get`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });
    
        const result = await response.json();
    
        if (result.status === 1) {
          setIsValidToken(true);
          setFormData(prev => ({
            ...prev,
            firstName: result.firstname,
            lastName: result.lastname,
            email: result.email,
          }));
          setSchoolName(result.schoolName);
        } else {
          setErrorMessage(t('register.invalidToken'));
          setIsValidToken(false);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setErrorMessage(t('register.tokenValidationFailed'));
        setIsValidToken(false);
      } finally {
        setIsLoadingToken(false);
      }
    };

    validateToken();
  }, [location.search, t]);

  useEffect(() => {
    if (!isLoadingToken && isValidToken) {
      const animatedElements = document.querySelectorAll('.animated-element');
      animatedElements.forEach((el, i) => {
        setTimeout(() => {
          el.classList.add('appear');
        }, i * 100);
      });
    }
  }, [isLoadingToken, isValidToken]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));

    if (formSubmitted) {
      validateField(name as keyof RegisterFormInputs, value);
    }
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setFormData(prev => ({...prev, country: value}));

    if (formSubmitted) {
      validateField('country', value);
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    setFormData(prev => ({...prev, timezone: value}));

    if (formSubmitted) {
      validateField('timezone', value);
    }
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }

    setFormSubmitted(true);

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      console.log("Form validation failed:", errors);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const ipAddress = ipInfo?.ip || 'unknown';
      const deviceData = deviceInfo || { type: 'unknown', browser: 'unknown', os: 'unknown', deviceName: 'unknown' };
      const accountType = "Teacher";
      const urlParams = new URLSearchParams(location.search);
      const studentToken = urlParams.get('token');

      const result = await registerUser(
        formData.schoolName,
        formData.firstName,
        formData.lastName,
        formData.username,
        formData.email,
        formData.password,
        ipAddress,
        formData.country,
        JSON.stringify(deviceData),
        formData.timezone,
        accountType,
        studentToken
      );

      console.log("Registration result:", result);

      if (!result.success) {
        console.error("Registration submission error:", result.error);

        if (result.data && result.data.errors) {
          // Handle validation errors from server
          const serverErrors = result.data.errors;
          let newFormErrors: {[key: string]: string} = {};

          if (serverErrors.username) {
            newFormErrors.username = t('register.usernameExists') || 'This username is already taken';
          }
          if (serverErrors.email) {
            newFormErrors.email = t('register.emailExists') || 'This email is already registered';
          }

          setFormErrors(prev => ({...prev, ...newFormErrors}));
        } else {
          // General error message
          setErrorMessage(result.error || t('register.registrationFailed'));
          toast({
            title: t('common.error'),
            description: result.error || t('register.registrationFailed'),
            variant: "destructive",
          });
        }
        return; // Important: stop execution here to prevent navigation
      }

      toast({
        title: t('register.success'),
        description: t('register.accountCreated'),
      });

      // Only navigate on success
      //navigate('/agenda', { replace: true });

    } catch (error: any) {
      console.error("Registration submission uncaught error:", error);
      setErrorMessage(error.message || t('register.registrationFailed'));
      toast({
        title: t('common.error'),
        description: error.message || t('register.registrationFailed'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    const form = document.querySelector('form');
    if (form) {
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true,
      });
      form.dispatchEvent(submitEvent);
    }
  };

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    console.log('Google register success:', credentialResponse);
    if (credentialResponse.credential) {
      loginWithGoogle(credentialResponse.credential);
    }
  };

  const handleGoogleError = () => {
    console.error('Google registration failed');
  };

  const handleAppleResponse = (response: any) => {
    if (response.authorization && response.authorization.id_token) {
      loginWithApple(response.authorization.id_token);
    } else if (response.error) {
      console.error('Apple login failed:', response.error);
    }
  };

  const selectedCountries = locale === 'fr' ? countries_fr : countries;

  if (isLoadingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="flex-1 flex flex-col justify-center p-8 md:p-12 lg:p-16">
        <div className="mx-auto w-full max-w-md">
          <div className="flex justify-between items-center mb-8">
            <Link
              to="/"
              className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>{t('nav.home')}</span>
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="animated-element mb-8">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {t('register.teacher.title')}
            </h1>
            {isValidToken && (
            <div className="animated-element mb-8">
              <p className="text-muted-foreground">
                {t('register.teacher.welcome', { schoolName: schoolName })}
              </p>
            </div>
          )}
          </div>

          

          {!isValidToken ? (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>{errorMessage || t('register.invalidToken')}</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-6">
              <div className="animated-element grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="country" className={`block text-sm font-medium ${formSubmitted && formErrors.country ? 'text-red-500' : ''}`}>
                    {t('common.country')} *
                  </label>
                  <div className="relative">
                    <Globe className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.country ? 'text-red-500' : 'text-muted-foreground'} z-10`} />
                    <Select
                      value={formData.country}
                      onValueChange={handleCountryChange}
                    >
                      <SelectTrigger className={`w-full pl-10 ${formSubmitted && formErrors.country ? 'border-red-500 ring-red-500' : ''}`}>
                        <SelectValue placeholder={t('common.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {selectedCountries.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {isLoadingIp && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  {formSubmitted && formErrors.country && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.country}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="timezone" className={`block text-sm font-medium ${formSubmitted && formErrors.timezone ? 'text-red-500' : ''}`}>
                    {t('common.timezone')} *
                  </label>
                  <div className="relative">
                    <Clock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.timezone ? 'text-red-500' : 'text-muted-foreground'} z-10`} />
                    <Select
                      value={formData.timezone}
                      onValueChange={handleTimezoneChange}
                    >
                      <SelectTrigger className={`w-full pl-10 ${formSubmitted && formErrors.timezone ? 'border-red-500 ring-red-500' : ''}`}>
                        <SelectValue placeholder={t('common.selectTimezone')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectGroup>
                          {availableTimezones.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {formatTimezone(tz)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  {formSubmitted && formErrors.timezone && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.timezone}</p>
                  )}
                </div>
              </div>

              <div className="animated-element grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className={`block text-sm font-medium ${formSubmitted && formErrors.firstName ? 'text-red-500' : ''}`}>
                    {t('common.firstName')} *
                  </label>
                  <div className="relative">
                    <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.firstName ? 'text-red-500' : 'text-muted-foreground'}`} />
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      autoComplete="given-name"
                      className={`pl-10 ${formSubmitted && formErrors.firstName ? 'border-red-500 ring-red-500' : ''}`}
                    />
                  </div>
                  {formSubmitted && formErrors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.firstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="lastName" className={`block text-sm font-medium ${formSubmitted && formErrors.lastName ? 'text-red-500' : ''}`}>
                    {t('common.lastName')} *
                  </label>
                  <div className="relative">
                    <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.lastName ? 'text-red-500' : 'text-muted-foreground'}`} />
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      autoComplete="family-name"
                      className={`pl-10 ${formSubmitted && formErrors.lastName ? 'border-red-500 ring-red-500' : ''}`}
                    />
                  </div>
                  {formSubmitted && formErrors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="animated-element space-y-2">
                <label htmlFor="email" className={`block text-sm font-medium ${formSubmitted && formErrors.email ? 'text-red-500' : ''}`}>
                  {t('common.email')} *
                </label>
                <div className="relative">
                   <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.email ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                    className={`pl-10 ${formSubmitted && formErrors.email ? 'border-red-500 ring-red-500 bg-gray-50' : 'bg-gray-50'}`}
                    readOnly
                  />
                </div>
                {/*formSubmitted && formErrors.email && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
                )*/}
              </div>

              <div className="animated-element space-y-2">
                <label htmlFor="username" className={`block text-sm font-medium ${formSubmitted && formErrors.username ? 'text-red-500' : ''}`}>
                  {t('common.username')} *
                </label>
                <div className="relative">
                  <Key className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.username ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleInputChange}
                    autoComplete="username"
                    className={`pl-10 ${formSubmitted && formErrors.username ? 'border-red-500 ring-red-500' : ''}`}
                  />
                </div>
                {formSubmitted && formErrors.username && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.username}</p>
                )}
              </div>

              <div className="animated-element space-y-2">
                <label htmlFor="password" className={`block text-sm font-medium ${formSubmitted && formErrors.password ? 'text-red-500' : ''}`}>
                  {t('common.password')} *
                </label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${formSubmitted && formErrors.password ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className={`pl-10 pr-10 ${formSubmitted && formErrors.password ? 'border-red-500 ring-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>
                {formSubmitted && formErrors.password && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>
                )}
              </div>

              <div className="animated-element pt-2">
                <Button
                  onClick={handleSubmit}
                  className="w-full py-6 text-base"
                  size="lg"
                  disabled={isSubmitting}
                  type="button"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </div>
                  ) : (
                    t('register.student.join_now')
                  )}
                </Button>
              </div>
            </div>
          )}

          {/** <div className="animated-element flex flex-row gap-3 justify-center mt-6 mb-6">
            <div className="w-full sm:w-auto flex justify-center">
              {!isInBlockingWebview ? (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="outline"
                  shape="rectangular"
                  type={!isMobile ? 'standard' : 'standard'}
                  text="continue_with"
                  size={!isMobile ? 'large' : 'medium'}
                  useOneTap={false}
                  containerProps={{ className: `w-full ${isMobile ? 'p-1' : 'rounded'}` }}
                />
              ) : (
                <GoogleWebviewAuth onClose={() => {}} />
              )}
            </div>

            <div className="w-1/2">
              <AppleLogin
                clientId="com.sportlogin.login"
                redirectURI={`${BASE_URL}/api/auth/apple`}
                usePopup={true}
                callback={handleAppleResponse}
                scope="email name"
                responseMode="query"
                render={renderProps => (
                  <Button
                    variant="outline"
                    className="w-full py-4 bg-white flex items-center justify-center gap-2 border border-gray-300"
                    onClick={renderProps.onClick}
                  >
                    <AppleIcon className="w-6 h-6" />
                    <span>{isMobile ? "Apple" : t('login.continueWithApple')}</span>
                  </Button>
                )}
              />
            </div>
          </div> */}

          <div className="animated-element mt-6 text-sm text-muted-foreground text-center">
            {t('register.terms')}
          </div>

        </div>
      </div>

      <div className="hidden md:block flex-1 bg-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50" />

        <div className="relative h-full flex items-center justify-center p-12">
          <div className="max-w-md">
            <div className="animated-element mb-8">
              <h2 className="text-2xl font-bold mb-4">
                Join this school and get started today!
              </h2>
              <p className="text-muted-foreground">
                Create your account and start enjoying these benefits:
              </p>
            </div>

            <ul className="space-y-4">
              {[
                'Smart scheduling that optimizes your routine',
                'Personalized progress tracking to reach your goals',
                'Secure and simplified payment processing',
                'Mobile apps available on iOS and Android',
                'Detailed performance analytics at your fingertips',
                '24/7 customer support to assist you'
              ].map((benefit, index) => (
                <li key={index} className="animated-element flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

           
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterTeacher;
